import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffTimetableService, ClassTimetableRows } from '../services/staff-timetable.service';
import { StaffSessionService } from '../services/staff-session.service';
import {
  TimelinePeriod,
  calculateTimelineMetrics,
  getTimeTopOffset,
  isPastPeriod,
  isCurrentPeriod,
  nowToMinutes,
  getCurrentWeekDays,
  WeekDay,
} from '../shared/staff-timetable-timeline';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

const HOUR_HEIGHT = 80;

// weekDaysMap: numeric day index (Date.getDay(), 0=Sun..6=Sat) → the
// response's per-day enabled flag field.
const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// The `periods` object is keyed by day NUMBER, Monday=1..Sunday=7 (ISO-style),
// unrelated to Date.getDay()'s Sunday=0 indexing. Maps Date.getDay() -> that key.
const PERIODS_DAY_NUMBER: { [jsDay: number]: string } = {
  0: '7', 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6',
};

@Component({
  selector: 'app-staff-class-timetable',
  templateUrl: './staff-class-timetable.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-timetable.component.css']
})
export class StaffClassTimetableComponent implements OnInit, OnDestroy {
  classId: string | null;
  className = '';

  loading = true;
  loadError = false;
  isConfigured = true; // false when API returns the "WIP" sentinel

  weekDays: WeekDay[] = getCurrentWeekDays();
  selectedDay: WeekDay = this.weekDays.find(d => d.isToday) || this.weekDays[0];

  timetableRows: ClassTimetableRows | null = null;
  periods: TimelinePeriod[] = [];
  hourTops: number[] = [];
  totalHeight = 24 * HOUR_HEIGHT;
  hourHeight = HOUR_HEIGHT;

  now = new Date();
  private nowTimer: any;
  private scrollTimer: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffTimetableService: StaffTimetableService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    const classData = this.staffSession.getClassData();
    this.className = classData ? `${classData.classNumber || ''} ${classData.classDivision || ''}`.trim() : '';

    this.nowTimer = setInterval(() => { this.now = new Date(); }, 60000);
    this.loadTimetable();
  }

  ngOnDestroy(): void {
    if (this.nowTimer) { clearInterval(this.nowTimer); }
    if (this.scrollTimer) { clearTimeout(this.scrollTimer); }
  }

  async loadTimetable() {
    if (!this.classId) { return; }
    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(this.staffTimetableService.getClassTimetable(this.classId));
      if (response && response.status && response.rows === 'WIP') {
        this.isConfigured = false;
        this.timetableRows = null;
      } else if (response && response.status && response.rows && (response.rows as ClassTimetableRows).isTimetable) {
        this.isConfigured = true;
        this.timetableRows = response.rows as ClassTimetableRows;
      } else {
        this.loadError = true;
        const unableToLoadTimetableTranslation = await firstValueFrom(this.translate.get('unableToLoadTimetable'));
        safeToast(response?.message || unableToLoadTimetableTranslation, 'error');
      }
    } catch (e) {
      this.loadError = true;
      const unableToLoadTimetableTranslation = await firstValueFrom(this.translate.get('unableToLoadTimetable'));
      safeToast(unableToLoadTimetableTranslation, 'error');
    } finally {
      this.loading = false;
      this.applySelectedDay();
    }
  }

  selectDay(day: WeekDay) {
    this.selectedDay = day;
    this.applySelectedDay();
  }

  private async applySelectedDay() {
    if (!this.timetableRows) {
      this.periods = [];
      this.hourTops = [];
      return;
    }
    const dayKey = WEEKDAY_KEYS[this.selectedDay.date.getDay()];
    const enabled = (this.timetableRows as any)[dayKey];
    const dayNumber = PERIODS_DAY_NUMBER[this.selectedDay.date.getDay()];
    const periodsForDay = enabled ? (this.timetableRows.periods[dayNumber] || {}) : {};
    const rawPeriods = Object.values(periodsForDay);

    const translations = await firstValueFrom(this.translate.get([
      'breakPeriod', 'lunchBreakPeriod', 'noSubjectAssigned', 'noStaffAssigned'
    ]));

    const normalized: TimelinePeriod[] = rawPeriods.map(p => ({
      periodId: String(p.periodId),
      periodStart: p.periodStart,
      periodEnd: p.periodEnd,
      title: p.periodType === 0 ? translations['breakPeriod'] : p.periodType === 2 ? translations['lunchBreakPeriod'] : (p.subject || translations['noSubjectAssigned']),
      subtitle: p.periodType === 1 ? (p.staffName || translations['noStaffAssigned']) : null,
      isBreak: p.periodType === 0 || p.periodType === 2,
    }));

    const metrics = calculateTimelineMetrics(normalized, this.hourHeight);
    this.periods = metrics.periods;
    this.hourTops = metrics.hourTops;
    this.totalHeight = metrics.totalHeight;

    this.scheduleAutoScroll();
  }

  private scheduleAutoScroll() {
    if (this.scrollTimer) { clearTimeout(this.scrollTimer); }
    this.scrollTimer = setTimeout(() => {
      const container = document.getElementById('sct-timeline-scroll');
      if (!container) { return; }
      let offset: number;
      if (this.selectedDay.isToday) {
        offset = getTimeTopOffset(this.currentTimeString, this.hourHeight);
      } else if (this.periods.length) {
        offset = this.periods[0].top || 0;
      } else {
        return;
      }
      container.scrollTo({ top: Math.max(offset - 100, 0), behavior: 'smooth' });
    }, 600);
  }

  get currentTimeString(): string {
    const h = String(this.now.getHours()).padStart(2, '0');
    const m = String(this.now.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  get currentTimeLabel(): string {
    const hours24 = this.now.getHours();
    const m = String(this.now.getMinutes()).padStart(2, '0');
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${m} ${period}`;
  }

  get nowOffset(): number {
    return getTimeTopOffset(this.currentTimeString, this.hourHeight);
  }

  get showNowLine(): boolean {
    return this.selectedDay.isToday;
  }

  hourLabel(hour: number): string {
    if (hour === 0 || hour === 24) { return '12 AM'; }
    if (hour === 12) { return '12 PM'; }
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  }

  isPast(p: TimelinePeriod): boolean {
    return isPastPeriod(this.selectedDay.isToday, p.periodEnd, nowToMinutes(this.now));
  }

  isCurrent(p: TimelinePeriod): boolean {
    return isCurrentPeriod(this.selectedDay.isToday, p.periodStart, p.periodEnd, nowToMinutes(this.now));
  }

  goToMyTimetable() {
    this.router.navigate(['/staff/my-timetable']);
  }

  goBack() {
    this.router.navigate(['/staff/class', this.classId]);
  }
}
